import http from '../http';
import Noun from '../core/Noun';
import { Response } from '../domain/Collection';
import {copyObject, findConnection} from '../libs';

function addQueryParam(key, value) {
  if (value === undefined || value === null) {
    // no-op
  } else if (!this._queryParams[key]) {
    this._queryParams[key] = [value];
  } else {
    this._queryParams[key].push(value);
  }
}

function addQueryParamSort(operator, attrName) {
  const operatorAndValue = `${operator}${attrName}`;

  return addQueryParam.call(this, 'sort', operatorAndValue);
}

function addQueryParamFilter(operator, attrName, value) {
  const operatorAndValue = operator === 'eq' ? value : `${operator}|${value}`;
  return addQueryParam.call(this, attrName, operatorAndValue);
}

// paramsArr is an array of an object that represents params
// the object can have either array or non-array keys. array keys will be appended as additional params.
function mergeParams(paramsArr) {
  const res = {};

  paramsArr = paramsArr.filter(v => !!v);

  function addParam(key, value) {
    if (res[key]) {
      res[key].push(value);
    } else {
      res[key] = [value];
    }
  }

  for (let i = 0; i < paramsArr.length; i++) {
    const params = paramsArr[i];

    for (const [key, value] of Object.entries(params)) {
      if (Array.isArray(value)) {
        for (let j = 0; j < value.length; j++) {
          addParam(key, value[j]);
        }
      } else {
        addParam(key, value);
      }
    }
  }

  return res;
}

class Link extends Noun {
  constructor(args = {}) {
    super(args);
    this.defaultExport = args.defaultExport;
    this.linkConnection = args.connection;
    this._queryParams = {};
  }

  id() {
    return this.attr('name');
  }

  // merge() handled automatically

  url(args = {}) {
    const { nullOnFailure = false } = args;

    try {
      let url = new URL(this.attr('url'));

      const urlParams = new URLSearchParams(url.search);

      const params = mergeParams([args.params, this._queryParams]);

      for (const [key, value] of Object.entries(params)) {
        if (Array.isArray(value)) {
          for (let i = 0; i < value.length; i++) {
            if (i === 0) {
              urlParams.set(key, value[i]);
            } else {
              urlParams.append(key, value[i]);
            }
          }
        } else {
          urlParams.set(key, value);
        }
      }

      url.search = urlParams.toString();

      return url.href; // Return a string of the whole URL
    } catch(err) {
      if (nullOnFailure) {
        return null;
      }

      throw err;
    }
  }

  isValid() {
    try {
      // Attempt to get the URL out of this, which will call new URL()
      this.url();

      return true;
    } catch {
      return false;
    }
  }

  connection() {
    if(this.linkConnection) {
      return this.linkConnection;
    }
    return findConnection(this.parent());
  }

  copy() {
    return copyObject(this, `link (url: ${this.url({ nullOnFailure: true })})`);
  }

  withQueryParams() {
    this._queryParams = {};

    return this;
  }

  sortAscending(attrName) {
    addQueryParamSort.call(this, '', attrName);

    return this;
  }

  sortDescending(attrName) {
    addQueryParamSort.call(this, '-', attrName);

    return this;
  }

  queryParamFilter(attrName, value, filter) {
    addQueryParamFilter.call(this, filter, attrName, value);

    return this;
  }

  in(attrName, value) {
    addQueryParamFilter.call(this, 'in', attrName, value);

    return this;
  }

  notIn(attrName, value) {
    addQueryParamFilter.call(this, 'nin', attrName, value);

    return this;
  }

  like(attrName, value) {
    addQueryParamFilter.call(this, 'like', attrName, value);

    return this;
  }

  equal(attrName, value) {
    addQueryParamFilter.call(this, 'eq', attrName, value);

    return this;
  }

  notEqual(attrName, value) {
    addQueryParamFilter.call(this, 'neq', attrName, value);

    return this;
  }

  greaterThan(attrName, value) {
    addQueryParamFilter.call(this, 'gt', attrName, value);

    return this;
  }

  greaterThanOrEqual(attrName, value) {
    addQueryParamFilter.call(this, 'gte', attrName, value);

    return this;
  }

  lessThan(attrName, value) {
    addQueryParamFilter.call(this, 'lt', attrName, value);

    return this;
  }

  lessThanOrEqual(attrName, value) {
    addQueryParamFilter.call(this, 'lte', attrName, value);

    return this;
  }

  limit(value) {
    addQueryParam.call(this, 'limit', value);

    return this;
  }

  offset(value) {
    addQueryParam.call(this, 'offset', value);

    return this;
  }

  get(args) {
    return this.Get(args);
  }

  Get(args = {}) {
    const conn = this.connection();
    return http.Get(this.url({ params: args.params }), { connection: conn, signal: args.signal }).then((res) => {
      if (args.raw) return res;

      return new Response({ data: res, metaOverrideSet: args.metaOverrideSet, connection: conn });
    });
  }

  delete(args) {
    return this.Delete(args);
  }

  async Delete(args = {}) {
    const conn = this.connection();
    const res = await http.Delete(this.url({ params: args.params }), { connection: conn, signal: args.signal });

    if (args.raw) return res;
    return new Response({ data: res, metaOverrideSet: args.metaOverrideSet, connection: conn});
  }

  post(d, args) {
    return this.Post(d, args);
  }

  Post(d, args = {}) {

    const data = d || this.defaultExport.export(args.exportArgs);

    const conn = this.connection();
    return http.Post(this.url({ params: args.params }), data, { connection: conn, signal: args.signal }).then((res) => {
      if (args.raw) return res;
      return new Response({ data: res, metaOverrideSet: args.metaOverrideSet, connection: conn });
    });
  }

  put(d, args) {
    return this.Put(d, args);
  }

  Put(d, args = {}) {

    const data = d || this.defaultExport.export(args.exportArgs);

    const conn = this.connection();
    return http.Put(this.url({ params: args.params }), data, { connection: conn, signal: args.signal }).then((res) => {
      if (args.raw) return res;
      return new Response({ data: res, metaOverrideSet: args.metaOverrideSet, connection: conn });
    });
  }

  param(key, args = {}) {
    const {
      firstValueOnly = true,
    } = args;
    if (key === undefined) {
      throw new Error('Usage: Key is required.');
    }

    let url = new URL(this.url());
    const urlParams = new URLSearchParams(url.search);
    if (firstValueOnly) {
      return urlParams.get(key);
    }
    return urlParams.getAll(key);
  }

  removeSearchParam(key) {
    if (key === undefined) {
      throw new Error('Usage: Key is required.');
    }

    let url = new URL(this.url());
    const urlParams = new URLSearchParams(url.search);
    urlParams.delete(key);
    delete this._queryParams[key];
    url.search = urlParams.toString();

    this.attr('url', url);

    return this;
  }
}

export default Link;
